// src/services/AiVoiceCallService.js
// AI ile LiveKit üzerinden 1:1 sesli görüşme için yardımcılar.
// Mevcut Agora/voice-room akışlarına dokunmaz; tamamen ayrı bir yol.
import { Room, RoomEvent, Track } from 'livekit-client'
import useJwt from '@/auth/jwt/useJwt'

/**
 * Backend'den AI sesli görüşme başlatır.
 * @param {string} aiUserUuid Hedef AI kullanıcının uuid'si.
 * @returns {Promise<object>} { call_id, room_name, server_url, participant_token, ai_user }
 */
export async function requestAiVoiceCall(aiUserUuid, language) {
  if (!aiUserUuid) {
    throw new Error('AI kullanıcı bilgisi eksik.')
  }
  const response = await useJwt.startAiVoiceCall(aiUserUuid, language)
  const body = response && response.data ? response.data.body : null
  if (!body || !body.server_url || !body.participant_token) {
    throw new Error('AI sesli görüşme başlatılamadı (geçersiz yanıt).')
  }
  return body
}

/**
 * LiveKit odasına bağlanır, mikrofonu yayınlar ve agent ses track'ini DOM'a bağlar.
 * @param {string} serverUrl wss:// LiveKit url
 * @param {string} token participant token
 * @param {object} handlers { onConnected, onDisconnected, onParticipantConnected, onParticipantDisconnected }
 * @returns {Promise<Room>} bağlı Room nesnesi
 */
export async function connectToAiRoom(serverUrl, token, handlers = {}) {
  const room = new Room({ adaptiveStream: true, dynacast: true })

  if (handlers.onConnected) room.on(RoomEvent.Connected, handlers.onConnected)
  if (handlers.onDisconnected) room.on(RoomEvent.Disconnected, handlers.onDisconnected)
  if (handlers.onParticipantConnected) room.on(RoomEvent.ParticipantConnected, handlers.onParticipantConnected)
  if (handlers.onParticipantDisconnected) room.on(RoomEvent.ParticipantDisconnected, handlers.onParticipantDisconnected)

  // Agent ses track'i geldiğinde gizli bir <audio>'ya bağla (otomatik çalar).
  room.on(RoomEvent.TrackSubscribed, track => {
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach()
      el.setAttribute('data-ai-voice-audio', 'true')
      el.style.display = 'none'
      document.body.appendChild(el)
    }
  })
  room.on(RoomEvent.TrackUnsubscribed, track => {
    track.detach().forEach(el => el.remove())
  })

  // Room nesnesini bağlantı tamamlanmadan önce çağırana ver; böylece kullanıcı
  // "bağlanıyor" aşamasında aramayı kapatırsa oda yine de kapatılabilir.
  if (handlers.onRoomCreated) handlers.onRoomCreated(room)

  try {
    await room.connect(serverUrl, token)
    await room.localParticipant.setMicrophoneEnabled(true)
  } catch (e) {
    // Bağlantı/mikrofon hatasında odayı açık bırakma (aksi halde AI arkada konuşmaya devam eder)
    await disconnectAiRoom(room)
    throw e
  }
  return room
}

/**
 * Mikrofonu aç/kapat.
 */
export async function setAiMicrophoneEnabled(room, enabled) {
  if (!room) return
  await room.localParticipant.setMicrophoneEnabled(enabled)
}

/**
 * Odadan ayrılır ve bağlı ses elementlerini temizler.
 */
function removeAiAudioElements() {
  document.querySelectorAll('[data-ai-voice-audio="true"]').forEach(el => {
    try {
      el.pause()
      el.srcObject = null
    } catch (e) { /* ignore */ }
    el.remove()
  })
}

export async function disconnectAiRoom(room) {
  // Olası takılı kalan ses elementlerini her durumda temizle.
  removeAiAudioElements()
  if (!room) return
  try {
    // Uzak ses track'lerini hemen sustur
    room.remoteParticipants.forEach(participant => {
      participant.trackPublications.forEach(pub => {
        if (pub.track) pub.track.detach().forEach(el => el.remove())
      })
    })
  } catch (e) { /* ignore */ }
  try {
    await room.localParticipant.setMicrophoneEnabled(false)
  } catch (e) { /* ignore */ }
  try {
    room.removeAllListeners()
    await room.disconnect(true)
  } catch (e) {
    console.error('[AiVoiceCallService] disconnect error:', e)
  }
  // disconnect sırasında eklenmiş olabilecek elementleri de temizle
  removeAiAudioElements()
}
